import {get, isEmpty, isNil, kebabCase, toUpper} from 'lodash'
import {itemPivotTableKey} from 'reducers/quotingContext/selected'
import {subItemPivotTableKey} from 'reducers/quotingContext/selected/itemDrawer'
import {canAddItemOfType, canAddMaterial, convertToMillimeters, isPartTypePricingCalculator} from "helpers/parts"
import {getBasePack, getDefaultPack} from "helpers/packs"
import {getPackTypeFor} from "helpers/packTypes"
import {OrderItemType, PricingCalculatorContext} from "helpers/orders"
import uuid from "helpers/uuid"
import {getCalculatedOrderItem, updateOrderItemUnitValuesAreSet} from "helpers/quotes"
import {isSavedSubItem} from "helpers/quoting/subItems"
import {parseIfNumeric} from "helpers/number"
import {
  calculatorInputTypes,
  features,
  PACK_TYPE_LABOUR_LABELS,
  PACK_TYPE_LABOUR_MINUTES,
  PartType,
  ScalingType,
} from "helpers/constants"
import {
  convertToCurrency,
  getFinancialValue,
  getMargin,
  getMarkup,
  getNumberString,
  getPriceFromMarkup,
  getProfit,
  getTotalValue,
  getUnicodeCharacter,
  getUnitCost,
  getUnitPrice,
  round,
} from "helpers/financial"
import {
  accountHasCalculator,
  accountHasFeature,
  isPricingCalculatorFeatureToggleEnabled,
  isPricingCalculatorPriceListEnabled,
} from "helpers/user"

export const TOKEN_SCOPE = 'calc:read,calc:write,calc:execute'
export const CALCULATOR_NAME = 'printingCalculator'
export const CALCULATOR_VERSION = 1

export const PRICING_METHODS = {
  AUTO_CALCULATED: 'autoCalculated',
  MARKUP_APPLIED: 'markupApplied',
  MARGIN_APPLIED: 'marginApplied',
  FIXED_PER_M2: 'fixedPerM2',
  PRICED_PER_UNIT: 'pricedPerUnit',
  FIXED_TOTAL: 'fixedTotal',
}

export const PRICING_METHOD_OPTIONS = [
  {
    key: PRICING_METHODS.AUTO_CALCULATED,
    value: PRICING_METHODS.AUTO_CALCULATED,
    label: 'Automatically calculated from component costs and markup',
  },
  {
    key: PRICING_METHODS.MARKUP_APPLIED,
    value: PRICING_METHODS.MARKUP_APPLIED,
    label: 'Markup applied to cost',
  },
  {
    key: PRICING_METHODS.MARGIN_APPLIED,
    value: PRICING_METHODS.MARGIN_APPLIED,
    label: 'Margin applied to cost',
  },
  {
    key: PRICING_METHODS.FIXED_PER_M2,
    value: PRICING_METHODS.FIXED_PER_M2,
    label: 'Fixed per square meter',
  },
  {
    key: PRICING_METHODS.PRICED_PER_UNIT,
    value: PRICING_METHODS.PRICED_PER_UNIT,
    label: 'Priced per unit',
  },
  {
    key: PRICING_METHODS.FIXED_TOTAL,
    value: PRICING_METHODS.FIXED_TOTAL,
    label: 'Fixed total, regardless of quantity',
  },
]

export const WASTE_OPTIONS = [
  {key: 'doNotCharge', value: 'doNotCharge', label: 'Do not charge for waste'},
  {key: 'chargeAtCost', value: 'chargeAtCost', label: 'Charge waste at cost'},
  {key: 'chargeAtCostPlusMarkup', value: 'chargeAtCostPlusMarkup', label: 'Charge waste at cost plus markup'},
]

export const DEFAULT_WASTE = 'chargeAtCost'

export const INPUT_LEVELS = {
  MASTER: 'master',
  ITEM: 'item',
}

const toHomeCurrency = (value, currencyRate) => value / currencyRate

export const DEFAULT_PRICING_CALCULATOR = 'printingCalculator@1'

export const pricingCalculatorOptions = [
  {
    key: DEFAULT_PRICING_CALCULATOR,
    value: DEFAULT_PRICING_CALCULATOR,
    label: 'Printing Calculator',
  },
]

export const getPricingCalculatorKey = (pricingCalculator) => {
  const name = get(pricingCalculator, 'name')
  const version = get(pricingCalculator, 'version')
  return isNil(name) || isNil(version) ? DEFAULT_PRICING_CALCULATOR : `${name}@${version}`
}

export const mapPricingOptions = (lineItemDetails, currencyRate = 1) => {
  const wasteCostMap = {
    'doNotCharge': 'exclude',
    'chargeAtCost': 'atCost',
    'chargeAtCostPlusMarkup': 'withMarkup',
  }
  const wasteCost = wasteCostMap[get(lineItemDetails, 'waste', 'doNotCharge')] || 'exclude'

  switch (get(lineItemDetails, 'pricingMethod')) {
    case PRICING_METHODS.MARKUP_APPLIED:
      return {
        pricingMethod: 'fixedMarkup',
        markup: get(lineItemDetails, 'markup', 0),
        wasteCost,
      }
    case PRICING_METHODS.MARGIN_APPLIED:
      return {
        pricingMethod: 'fixedMargin',
        margin: get(lineItemDetails, 'margin', 0),
        wasteCost,
      }
    case PRICING_METHODS.FIXED_PER_M2:
      return {
        pricingMethod: 'fixedPricePerArea',
        pricePerArea: toHomeCurrency(get(lineItemDetails, 'fixedPriceM2', 0), currencyRate),
        wasteCost,
      }
    case PRICING_METHODS.PRICED_PER_UNIT:
      return {
        pricingMethod: 'fixedPerUnit',
        perUnit: toHomeCurrency(get(lineItemDetails, 'unitPrice', 0), currencyRate),
        wasteCost,
      }
    case PRICING_METHODS.FIXED_TOTAL:
      return {
        pricingMethod: 'fixedTotal',
        fixedTotal: toHomeCurrency(get(lineItemDetails, 'totalPrice', 0), currencyRate),
        wasteCost,
      }
    case PRICING_METHODS.AUTO_CALCULATED:
    default:
      return {
        pricingMethod: 'auto',
        wasteCost,
      }
  }
}

export const mapOrderItemToLineItemDetails = (orderItem) => {
  const calculatorInputs = get(orderItem, 'calculatorData.inputs', [])

  const getInput = (key, defaultValue) => {
    const input = calculatorInputs.find(i => i.key === key)
    if (!input) return defaultValue
    if (input.valueType === calculatorInputTypes.NUMBER) return input.numberValue
    if (input.valueType === calculatorInputTypes.BOOLEAN) return input.boolValue
    if (input.valueType === calculatorInputTypes.TEXT) return input.textValue
    if (input.valueType === calculatorInputTypes.PART) return input.partId
    return defaultValue
  }

  const desc = getInput('description', null)
  const notes = getInput('notes', null)

  const pricingMethod = getInput('pricingMethod', null) || PRICING_METHODS.AUTO_CALCULATED

  return {
    partCode: get(orderItem, 'partCode', 'ITEM'),
    description: isNil(desc) ? get(orderItem, 'desc', '') : desc,
    pricingMethod,
    waste: getInput('waste', 'chargeAtCost'),
    unitCost: get(orderItem, 'unitCost', 0),
    markup: get(orderItem, 'markup', 0),
    margin: get(orderItem, 'margin', 0),
    unitPrice: get(orderItem, 'unitPrice', 0),
    totalPrice: get(orderItem, 'totalPrice', 0),
    fixedPriceM2: pricingMethod === PRICING_METHODS.FIXED_PER_M2
      ? getInput('fixedPriceM2', 0)
      : 0,
    taxCode: getInput('taxCode', null) || get(orderItem, 'taxCodeId', 'standard'),
    notes: isNil(notes) ? get(orderItem, 'notes', '') : notes,
  }
}

export const calculatePricing = (calculationResult, pricingAccuracy, quantity = 1, currencyRate = 1) => {
  const unitCost = round(
    getUnitCost(get(calculationResult, 'totalCost', 0), quantity),
    pricingAccuracy
  )
  const unitPrice = round(
    getUnitPrice(convertToCurrency(get(calculationResult, 'totalPrice', 0), currencyRate), quantity),
    pricingAccuracy
  )
  const totalCost = getTotalValue(unitCost, quantity)
  const totalPrice = getTotalValue(unitPrice, quantity)
  const markup = round(getMarkup(unitPrice, unitCost, currencyRate), 2)
  const margin = round(getMargin(unitPrice, unitCost, currencyRate), 2)
  const profit = round(getProfit(totalPrice, totalCost, currencyRate), 2)

  return {totalCost, totalPrice, unitCost, unitPrice, markup, margin, profit}
}

export const mapCalculatorItemsToSubItems = (items, parentItem, parts = {}, packTypes = []) => {
  if (isEmpty(items)) return []

  const currencyRate = get(parentItem, 'currencyRate', 1)

  return items.map(item => {
    const subGuid = uuid()
    const quantity = get(item, 'quantity', 1)
    const unitCost = get(item, 'unitCost', 0)
    const markup = get(item, 'markup', 0)
    const unitPrice = getPriceFromMarkup(unitCost, markup, currencyRate)
    const totalCost = round(unitCost * quantity, 2)
    const totalPrice = round(unitPrice * quantity, 2)
    const itemId = get(item, 'id', null)
    const part = (itemId && parts[itemId]) || null

    const isLabour = isLabourPart(part)
    const minutesPack = isLabour ? getMinutesPackForPart(part, packTypes) : null

    return {
      isNew: true,
      enabled: true,
      id: subGuid,
      guid: subGuid,
      parentId: get(parentItem, 'id'),
      desc: get(item, 'description', ''),
      partCode: get(item, 'partCode', ''),
      part,
      partId: itemId,
      quantity,
      unitCost,
      unitPrice,
      unitCostSet: false,
      unitPriceSet: false,
      totalCost,
      totalPrice,
      profit: round(getProfit(totalPrice, totalCost, currencyRate), 2),
      length: get(item, 'length', 0),
      width: get(item, 'width', 0),
      scalingType: ScalingType.FIXED.id,
      discount: 0,
      type: {id: OrderItemType.pricing_calculator},
      partType: get(item, 'partType', null),
      manCode: get(item, 'manCode', null),
      weight: get(item, 'weight', 0),
      partPackId: isLabour
        ? get(minutesPack, 'id', null)
        : get(getPricingPack(part), 'id', null),
      partPackDesc: get(item, 'partPackDesc', null),
      markup: unitCost > 0 ? round(getMarkup(unitPrice, unitCost, currencyRate), 2) : 0,
      margin: unitCost > 0 ? round(getMargin(unitPrice, unitCost, currencyRate), 2) : 0,
      dormant: false,
      nominalCode: null,
      notes: '',
      taxCode: get(parentItem, 'taxCode', null),
      taxRate: get(parentItem, 'taxRate', 0),
      taxCodeId: get(parentItem, 'taxCodeId', null),
      referralFee: get(parentItem, 'referralFee', 0),
      currencyRate,
      pricingRate: 1,
    }
  })
}

export const reparentSubItems = (subItems, parentId) =>
  (subItems || []).map(subItem => ({...subItem, parentId}))

export const isAutoCalculatedPricingMethodSelected = (formData) => {
  const pricingMethod = get(formData, 'lineItemDetails.pricingMethod')
  return pricingMethod === PRICING_METHODS.AUTO_CALCULATED
}

export const partHasLengthAndWidth = (part) => {
  const length = get(part, 'sizeX', null)
  const width = get(part, 'sizeY', null)

  return !isNil(length) && !isNil(width) && length > 0 && width > 0
}

export const isPartSelectable = (part, user, allowedTypes, input, formValues) => {
  return canAddMaterial(part.type, part.status, user, allowedTypes) &&
    !showAddDimensionsHint(part, input) &&
    !showNarrowerThanReferenceHint(part, input, formValues)
}

const DIMENSION_HINT_PART_TYPES = [
  PartType.MATERIAL.id,
  PartType.NON_STOCK.id,
  PartType.OUTSOURCED.id,
  PartType.SERVICE.id,
]

const shouldHaveDimensions = (part) => {
  return DIMENSION_HINT_PART_TYPES.includes(part.type)
}

export const ADD_DIMENSIONS_HINT = 'Add a length and width to this part before it can be used'

export const partInputRequiresDimensions = (input) => get(input, 'options.withDimensions') !== false

export const partInputShowsDimensions = (input) => get(input, 'options.showDimensions') !== false

export const showAddDimensionsHint = (part, input) => {
  return partInputRequiresDimensions(input) && shouldHaveDimensions(part) && !partHasLengthAndWidth(part)
}

export const NARROWER_THAN_SUBSTRATE_HINT =
  `This part can't be used as a laminate material as it's narrower than the material you're printing to`

const getWidthInMillimetres = (part) =>
  convertToMillimeters(get(part, 'sizeY', null), get(part, 'lengthClass'))

// Widths are compared as converted millimetres, never the rounded display values.
export const showNarrowerThanReferenceHint = (part, input, formValues) => {
  const referenceInputName = get(input, 'options.minWidthFrom')
  if (!referenceInputName) {
    return false
  }

  const minimumWidth = getWidthInMillimetres(get(formValues, [referenceInputName, 'part']))
  const width = getWidthInMillimetres(part)

  return isNil(minimumWidth) || isNil(width)
    ? false
    : width < minimumWidth
}

export const getPartSelectionHint = (part, input, formValues) => {
  return showAddDimensionsHint(part, input)
    ? ADD_DIMENSIONS_HINT
    : showNarrowerThanReferenceHint(part, input, formValues)
      ? NARROWER_THAN_SUBSTRATE_HINT
      : ''
}

const isDynamicInputPopulated = (input, value) => {
  switch (input.type) {
    case 'part':
      return !!get(value, 'part.id')
    case 'number':
      return value !== null && value !== undefined && !isNaN(value)
    case 'boolean':
      return typeof value === 'boolean'
    case 'resource':
    case 'resourceSubType':
      return !!value
    case 'edgeSelection':
      return !isEmpty(parseEdgeSelection(value))
    default:
      return !isNil(value)
  }
}

// Older form definitions provided a flat inputs list rather than rows; treat each such input as its own row.
export const getInputRows = (section) => {
  const inputs = get(section, 'inputs', []) || []
  const isGroupedIntoRows = Array.isArray(inputs[0])
  return isGroupedIntoRows ? inputs : inputs.map(input => [input])
}

export const getSectionInputs = (section) => getInputRows(section).flat()

export const findInput = (inputs, name) => {
  return inputs.find(input => input.name === name)
}

export const getFormInputDefinitions = (formDefinition) =>
  get(formDefinition, 'form.sections', [])
    .flatMap(getSectionInputs)
    .map(inputName => findInput(get(formDefinition, 'inputs', []), inputName))
    .filter(Boolean)

// The flat list, not the sections: the form renders one section at a time.
const getResourceSubTypeInputs = (formDefinition) =>
  get(formDefinition, 'inputs', []).filter(input => input.type === 'resourceSubType')

const getDependentResourceSubTypes = (formDefinition, resourceInputName) =>
  getResourceSubTypeInputs(formDefinition)
    .filter(input => get(input, 'options.filteredBy') === resourceInputName)

const hasResourceValue = (value) => !isNil(value) && value !== ''

const isResourceInput = (input) => ['resource', 'resourceSubType'].includes(get(input, 'type'))

const withValue = (inputs, name, value) => {
  const current = get(inputs, name)
  const isUnchanged = isNil(value) ? isNil(current) : current === value
  return isUnchanged ? inputs : {...inputs, [name]: value}
}

export const resolveDependentResourceSubTypes = (formDefinition, changedInputName, inputs, resources) => {
  const dependents = getDependentResourceSubTypes(formDefinition, changedInputName)

  if (isEmpty(dependents)) return inputs

  const resolved = {...inputs}
  dependents.forEach(depInput => {
    const currentValue = inputs[depInput.name]
    const [firstOption] = getResourceSubTypeOptions(depInput, resolved, resources, currentValue)
    resolved[depInput.name] = firstOption ? firstOption.key : null
  })
  return resolved
}

export const syncResourceSubTypes = (formDefinition, inputs, resources) => {
  const subTypeInputs = getResourceSubTypeInputs(formDefinition)
  if (isEmpty(subTypeInputs)) return inputs

  return subTypeInputs.reduce((resolved, input) => {
    if (!hasResourceValue(get(resolved, get(input, 'options.filteredBy'))))
      return withValue(resolved, input.name, null)

    const currentValue = inputs[input.name]
    const options = getResourceSubTypeOptions(input, resolved, resources, currentValue)

    if (isEmpty(options) || options.some(option => option.key === get(resolved, input.name)))
      return resolved

    return withValue(resolved, input.name, options[0].key)
  }, inputs)
}

const byLabel = (a, b) => (a.label || '').localeCompare(b.label || '')

export const getDisplayFieldTexts = (input, source) => {
  return get(input, 'options.displayFields', [])
    .map(field => {
      const value = get(source, field.fieldName)
      if (isNil(value) || value === '') return null
      const uom = get(field, 'uom', '')
      return uom ? `${value} ${uom}` : `${value}`
    })
    .filter(Boolean)
}

export const getResourceSelectionHint = (input, resource, formDefinition) => {
  if (isEmpty(getDependentResourceSubTypes(formDefinition, input.name))) return null

  const usableModes = (get(resource, 'modes') || []).filter(mode => !get(mode, 'dormant'))
  if (!isEmpty(usableModes)) return null

  const resourceNoun = get(input, 'label', 'resource').toLowerCase()
  return `Add a mode to this ${resourceNoun} before it can be used`
}

export const getResourceOptions = (input, resources, formDefinition, currentValue) => {
  const resourceTypeName = get(input, 'options.resourceType', '')
  if (!resourceTypeName) return []

  return Array.from(resources || [])
    .filter(r => canSelectResource(r, resourceTypeName, currentValue))
    .map(r => ({
      key: r.id,
      label: r.name + (r.dormant ? ' [DORMANT]' : ''),
      details: getDisplayFieldTexts(input, r),
      disabledHint: getResourceSelectionHint(input, r, formDefinition),
    }))
    .sort(byLabel)
}

export const canSelectResource = (resource, resourceTypeName, currentValue) => {
  const isMatchingType = resource.type && resource.type.toLowerCase() === resourceTypeName.toLowerCase()
  const isDormant = get(resource, 'dormant', false)
  const isCurrentValue = resource.id === currentValue

  return isMatchingType && (isCurrentValue || !isDormant)
}

export const getResourceSubTypeOptions = (input, formValues, resources, currentValue) => {
  const filteredBy = get(input, 'options.filteredBy')
  if (!filteredBy) return []

  const selectedResourceId = get(formValues, filteredBy)
  if (!hasResourceValue(selectedResourceId)) return []

  const resource = Array.from(resources || [])
    .find(r => String(r.id) === String(selectedResourceId))

  const usableModes = (get(resource, 'modes', []) || [])
    .filter(mode => canSelectResourceMode(mode, currentValue))

  return Array.from(usableModes, mode => ({
    key: mode.name,
    label: mode.name + (mode.dormant ? ' [DORMANT]' : ''),
    details: getDisplayFieldTexts(input, mode),
  }))
}

export const canSelectResourceMode = (mode, currentValue) => {
  const isDormant = get(mode, 'dormant', false)
  const isCurrentValue = mode.name === currentValue
  return isCurrentValue || !isDormant
}

export const isDynamicInputRequired = (input, formValues, resources, currentValue) => {
  if (get(input, 'type') !== 'resourceSubType')
    return get(input, 'required', false)

  return !isEmpty(getResourceSubTypeOptions(input, formValues, resources, currentValue))
}

export const hasAllRequiredInputs = (dynamicInputs, formDefinition, resources) => {
  return getFormInputDefinitions(formDefinition).every(input => {
    const currentValue = dynamicInputs[input.name]

    return (
      !isDynamicInputRequired(input, dynamicInputs, resources, currentValue)
      || isDynamicInputPopulated(input, get(dynamicInputs, input.name))
    )
  })
}

const mapPartToRequestInput = (part, pricing, currencyRate = 1) => {
  const unitCost = get(pricing, 'unitCost', 0)

  return {
    id: get(part, 'id', ''),
    partCode: get(part, 'partCode'),
    description: get(part, 'description', ''),
    name: get(part, 'description', ''),
    length: convertToMillimeters(get(part, 'sizeX', 0), get(part, 'lengthClass')),
    width: convertToMillimeters(get(part, 'sizeY', 0), get(part, 'lengthClass')),
    unitCost: unitCost,
    markup: getMarkup(get(pricing, 'unitPrice', 0), unitCost, currencyRate),
    manCode: get(part, 'manCode', null),
    weight: get(part, 'weight', 0),
    partType: get(part, 'type', null),
    packTypeId: get(getPricingPack(part), 'packTypeId', null),
  }
}

export const mapDynamicFormDataToRequest = (
  dynamicInputs,
  formDefinition,
  pricingByInputName = {},
  lineItemDetails = {},
  packTypes = [],
  currencyRate = 1
) => {
  const inputs = {}

  formDefinition.inputs.forEach(input => {
    const value = get(dynamicInputs, input.name)
    if (value === undefined || value === null) return

    if (isResourceInput(input) && !hasResourceValue(value)) return

    if (input.type === 'part') {
      const part = get(value, 'part', {})
      const pricing = get(pricingByInputName, input.name, {unitCost: 0, unitPrice: 0})
      inputs[input.name] = mapPartToRequestInput(
        part,
        isLabourPart(part) ? getLabourPricingPerMinute(part, pricing, packTypes) : pricing,
        currencyRate
      )
    } else {
      inputs[input.name] = value
    }
  })

  inputs.description = lineItemDetails.description
  inputs.notes = lineItemDetails.notes

  return {
    processes: get(formDefinition, 'processes', ['printing@1']),
    inputs,
    pricing: mapPricingOptions(lineItemDetails, currencyRate),
  }
}

export const isLabourPart = (part) => get(part, 'type', null) === PartType.LABOUR.id

export const getMinutesPerPackUnit = (pack, packTypes) => {
  const multiple = get(pack, 'multiple', 0)
  if (multiple > 0) {
    return multiple
  }
  const description = get(getPackTypeFor(get(pack, 'packTypeId', null), packTypes), 'description', null)
  return PACK_TYPE_LABOUR_MINUTES[toUpper(description)] || 1
}

export const getPricingPack = (part) => {
  const packs = get(part, 'packs', [])
  return getDefaultPack(packs) || getBasePack(packs)
}

export const getLabourPricingPerMinute = (part, pricing, packTypes) => {
  const minutesPerUnit = getMinutesPerPackUnit(getPricingPack(part), packTypes)

  return {
    unitCost: get(pricing, 'unitCost', 0) / minutesPerUnit,
    unitPrice: get(pricing, 'unitPrice', 0) / minutesPerUnit,
  }
}

export const getMasterPartInputs = (master) => {
  return get(master, 'inputs', [])
    .filter(input => get(input, 'valueType') === calculatorInputTypes.PART)
    .map(input => ({key: input.key, part: get(input, 'parts', null)}))
    .filter(({part}) => !isNil(get(part, 'id', null)))
}

export const mapMasterPartInputsToRequest = (masterPartInputs, pricingByKey = {}, packTypes = []) => {
  const inputs = {}

  masterPartInputs.forEach(({key, part}) => {
    const pricing = get(pricingByKey, key, {unitCost: 0, unitPrice: 0})

    inputs[key] = mapPartToRequestInput(
      part,
      isLabourPart(part) ? getLabourPricingPerMinute(part, pricing, packTypes) : pricing
    )
  })

  return inputs
}

const getMinutesPackForPart = (part, packTypes) => {
  const minutesPackTypeIds = (packTypes || [])
    .filter(packType => get(packType, 'description') === PACK_TYPE_LABOUR_LABELS.MINUTES)
    .map(packType => packType.id)

  if (isEmpty(minutesPackTypeIds)) return null

  return get(part, 'packs', []).find(pack => minutesPackTypeIds.includes(pack.packTypeId)) || null
}

export const getCalculatedPricePerM2 = (calculationResult, totalPrice) => {
  const usableArea = get(calculationResult, 'additional.nestingSummary.usableArea', 0)
  if (usableArea <= 0) return 0
  return totalPrice / (usableArea / 1000000)
}

const getInputRawValue = (input) => {
  switch (input.valueType) {
    case calculatorInputTypes.NUMBER:
      return input.numberValue
    case calculatorInputTypes.BOOLEAN:
      return input.boolValue
    case calculatorInputTypes.TEXT:
      return input.textValue
    case calculatorInputTypes.PART:
      return input.partId
    default:
      return null
  }
}

const updateCalculatorInput = (input, newValue) => {
  switch (input.valueType) {
    case calculatorInputTypes.NUMBER:
      input.numberValue = newValue
      break
    case calculatorInputTypes.BOOLEAN:
      input.boolValue = newValue
      break
    case calculatorInputTypes.TEXT:
      input.textValue = newValue
      break
  }
}

export const updateOrderItemFromCalculatorResult = (
  orderItem,
  calculationResult,
  updatedCalcData,
  resolvedParts,
  pricingAccuracy,
  packTypes = []
) => {
  const quantity = get(orderItem, 'quantity', 1)
  const currencyRate = get(orderItem, 'currencyRate', 1)

  const {
    totalCost,
    totalPrice,
    unitCost,
    unitPrice,
    markup,
    margin,
    profit,
  } = calculatePricing(calculationResult, pricingAccuracy, quantity, currencyRate)

  const updatedItem = {
    ...orderItem,
    totalCost,
    totalPrice,
    unitCost,
    unitPrice,
    unitCostSet: true,
    unitPriceSet: true,
    markup,
    margin,
    profit,
    calculatorData: updatedCalcData,
  }

  const parts = {}
  Object.keys(resolvedParts).forEach(partId => {
    parts[partId] = {id: partId, ...resolvedParts[partId]}
  })

  // Collect IDs of existing subitems so the backend knows to delete them.
  // Carry forward any previously accumulated deletedSubItems so that
  // consecutive inline edits don't lose the original server IDs.
  const existingSubItems = get(orderItem, 'subItems', [])
  const previouslyDeleted = get(orderItem, 'deletedSubItems', [])
  const newlyDeleted = existingSubItems
    .filter(si => si.id && !si.isNew)
    .map(si => si.id)
  updatedItem.deletedSubItems = [...new Set([...previouslyDeleted, ...newlyDeleted])]

  const calculatorItems = get(calculationResult, 'items', [])
  updatedItem.subItems = calculatorItems.length > 0
    ? mapCalculatorItemsToSubItems(calculatorItems, updatedItem, parts, packTypes)
    : []

  return updatedItem
}

export const getCalculatorInputName = (entity, colKey, fieldMappings) => {
  if (get(entity, 'type.id') !== OrderItemType.pricing_calculator) return null
  if (!get(entity, 'calculatorData')) return null
  const calcKey = `${get(entity, 'calculatorData.name')}/${get(entity, 'calculatorData.version')}`
  const mappings = fieldMappings[calcKey]
  if (!mappings) return null
  return Object.keys(mappings).find(k => mappings[k] === colKey) || null
}

export const hasAnyPricingCalculatorItems = (orderItems) => {
  return orderItems.some(item => isPricingCalculatorEntity(item))
}

export const isPricingCalculatorEntity = (entity) =>
  get(entity, 'type.id') === OrderItemType.pricing_calculator && !isNil(get(entity, 'calculatorData'))

export const isCalculatorBearingPivotKey = key =>
  key === itemPivotTableKey || key === subItemPivotTableKey

// A summary entry may only apply to some calculations — material used is a length
// on a roll-fed printer and a sheet count on a flatbed. The engine resolves which
// applies per run and reports it in the result's `display` map, keyed by entry key.
// Before any calculation has run there is no result, so the entry's own `hidden`
// default from the form definition decides. An entry with neither is always shown.
export const isEntryVisible = ({hidden}, resolved) => {
  const resolvedHidden = get(resolved, 'hidden')
  return !(isNil(resolvedHidden) ? hidden : resolvedHidden)
}

export const resolveUom = ({uom, uomSingular, uomPlural}, value) => {
  if (isNil(uomSingular) && isNil(uomPlural))
    return uom

  return Number(value) === 1 ? uomSingular : uomPlural
}

export const formatMinutesAsHoursAndMinutes = (value) => {
  const minutes = Math.max(0, Math.round(value || 0))
  return `${Math.floor(minutes / 60)} Hrs ${minutes % 60} Min`
}

export const formatSummary = (value, format, decimals, currency, uom) => {
  switch (format) {
    case 'number':
      return getNumberString(value, decimals) + uom

    case 'currency':
      const currencySymbol = getUnicodeCharacter(currency.unicodePattern)
      const formattedValue = getFinancialValue(value, 2)
      return currencySymbol + formattedValue

    case 'duration':
      return formatMinutesAsHoursAndMinutes(value)

    default:
      return value + uom
  }
}

export const buildDynamicCalculatorData = (dynamicInputs, lineItemDetails, formDefinition) => {
  const inputs = createInputsFromFormValues(formDefinition, dynamicInputs)

  const lid = lineItemDetails || {}
  inputs.push(
    {
      key: 'pricingMethod',
      valueType: calculatorInputTypes.TEXT,
      textValue: get(lid, 'pricingMethod', 'autoCalculated'),
    },
    {key: 'fixedPriceM2', valueType: calculatorInputTypes.NUMBER, numberValue: get(lid, 'fixedPriceM2', 0)},
    {key: 'waste', valueType: calculatorInputTypes.TEXT, textValue: get(lid, 'waste', 'chargeAtCost')},
    {key: 'description', valueType: calculatorInputTypes.TEXT, textValue: get(lid, 'description', '')},
    {key: 'notes', valueType: calculatorInputTypes.TEXT, textValue: get(lid, 'notes', '')}
  )

  return {
    name: get(formDefinition, 'name', 'printingCalculator'),
    version: get(formDefinition, 'version', 1),
    processes: (get(formDefinition, 'processes', ['materialCosts@1']) || []).join(','),
    inputs,
  }
}

export const createInputsFromFormValues = (formDefinition, formValues, ownLevel = 'ITEM') => {
  const inputs = []

  Object.keys(formValues).forEach(inputName => {
    const input = findInput(formDefinition.inputs, inputName)
    if (input) {
      const value = get(formValues, inputName)
      inputs.push(createInputValueFromFormValue(input, value, ownLevel))
    }
  })

  return inputs
}

const createInputValueFromFormValue = (input, value, ownLevel) => {
  const level = input.level === 'master' ? 'MASTER' : ownLevel

  if (isResourceInput(input))
    return {
      key: input.name,
      valueType: calculatorInputTypes.TEXT,
      textValue: hasResourceValue(value) ? String(value) : null,
      level,
    }

  switch (input.type.toUpperCase()) {

    case calculatorInputTypes.PART:
      return {
        key: input.name,
        valueType: calculatorInputTypes.PART,
        partId: get(value, 'part.id', null),
        parts: get(value, 'part', null),
        level,
      }

    case calculatorInputTypes.NUMBER:
      return {
        key: input.name,
        valueType: calculatorInputTypes.NUMBER,
        numberValue: value ?? null,
        level,
      }

    case calculatorInputTypes.BOOLEAN:
      return {
        key: input.name,
        valueType: calculatorInputTypes.BOOLEAN,
        boolValue: value ?? false,
        level,
      }

    default:
      return {
        key: input.name,
        valueType: calculatorInputTypes.TEXT,
        textValue: value != null ? String(value) : '',
        level,
      }
  }
}

export const mapDynamicResultToOrderItem = (
  dynamicInputs,
  lineItemDetails,
  calculationResult,
  order,
  taxCodes,
  formDefinition,
  pricingAccuracy,
  packTypes = []
) => {
  const lid = lineItemDetails || {}

  const taxCodeId = get(lid, 'taxCode', get(order, 'taxCodeId'))
  const taxCodeObj = (taxCodes || []).find(tc => tc.id === taxCodeId)
  const taxRate = get(taxCodeObj, 'rate', get(order, 'taxRate', 0))

  const calculatorData = buildDynamicCalculatorData(dynamicInputs, lid, formDefinition)

  const guid = uuid()
  const baseItem = {
    isNew: true,
    id: guid,
    guid,
    partCode: get(lid, 'partCode', 'ITEM'),
    desc: get(calculationResult, 'description', get(lid, 'description', '')),
    notes: get(calculationResult, 'notes', get(lid, 'notes', '')),
    taxCode: taxCodeObj || null,
    taxRate,
    taxCodeId,
    quantity: get(calculationResult, 'quantity', 1),
    length: get(calculationResult, 'length', 0),
    width: get(calculationResult, 'width', 0),
    height: get(calculationResult, 'height', 0),
    referralFee: get(order, 'referralFee', 0),
    currencyRate: get(order, 'currencyRate', 1),
    dormant: false,
    netTax: 0,
    netPrice: 0,
    netMarkup: 0,
    netMargin: 0,
    order: null,
    orderId: null,
    parentId: undefined,
    part: null,
    partId: null,
    partPackDesc: 'Unit(s)',
    partPackId: null,
    msrp: null,
    partType: PartType.GO_PRICING_CALCULATOR.id,
    type: {id: OrderItemType.pricing_calculator},
    scalingType: ScalingType.VARIABLE.id,
    pricingRate: 1,
    useMSRPAsPrice: false,
    supplierDiscount: 0,
    supplierCompanyId: null,
  }

  const resolvedParts = {}
  formDefinition.inputs.forEach(input => {
    if (input.type === 'part') {
      const part = get(dynamicInputs, `${input.name}.part`)
      if (part && part.id) resolvedParts[part.id] = part
    }
  })

  return updateOrderItemFromCalculatorResult(
    baseItem,
    calculationResult,
    calculatorData,
    resolvedParts,
    pricingAccuracy,
    packTypes
  )
}

export const buildFormInputsFromOrderItem = (orderItem, latestInputs) => {
  const inputs = get(orderItem, 'calculatorData.inputs', [])
  return buildFormInputs(isNil(latestInputs) ? inputs : mergeLatestHigherLevelInputs(inputs, latestInputs))
}

const isOwnLevelInput = (input, ownLevel) => isNil(input.level) || toUpper(input.level) === ownLevel

// Master/part inputs on an item are only a copy taken when the quote was loaded, so swap them
// for the latest ones. Item inputs win over the latest ones, matching the backend's resolution.
// A Price List calculator part passes PART as its own level, so only its master inputs are swapped.
export const mergeLatestHigherLevelInputs = (itemInputs = [], latestInputs = [], ownLevel = 'ITEM') => {
  const ownInputs = itemInputs.filter(input => isOwnLevelInput(input, ownLevel))
  const ownKeys = new Set(ownInputs.map(input => input.key))

  return [
    ...ownInputs,
    ...latestInputs.filter(input => !isOwnLevelInput(input, ownLevel) && !ownKeys.has(input.key)),
  ]
}

export const createCalculatorDataFromPart = (part, formDefinition, masterInputs = []) => {
  const pricingCalculator = get(part, 'pricingCalculator')
  if (isNil(pricingCalculator)) return null

  const inputs = get(pricingCalculator, 'inputs', [])
    .filter(input => isOwnLevelInput(input, 'PART'))
    .map(input => ({...input, level: 'ITEM'}))

  return {
    name: get(pricingCalculator, 'name'),
    version: get(pricingCalculator, 'version'),
    processes: (get(formDefinition, 'processes') || []).join(','),
    inputs: mergeLatestHigherLevelInputs(inputs, masterInputs),
  }
}

export const hasCalculatorCostOrPriceChanged = (orderItem, calculationResult, pricingAccuracy) => {
  if (!orderItem || !calculationResult) return false

  const {
    totalCost,
    totalPrice,
  } = calculatePricing(calculationResult, pricingAccuracy, get(calculationResult, 'quantity', 1))
  const savedQuantity = get(orderItem, 'quantity', 1)
  const savedTotalCost = get(orderItem, 'totalCost', getTotalValue(get(orderItem, 'unitCost', 0), savedQuantity))
  const savedTotalPrice = get(orderItem, 'totalPrice', getTotalValue(get(orderItem, 'unitPrice', 0), savedQuantity))

  return round(totalCost, 2) !== round(savedTotalCost, 2)
    || round(totalPrice, 2) !== round(savedTotalPrice, 2)
}

export const buildFormInputs = (inputs) => {
  const formInputs = {}

  inputs.forEach(input => {
    if (input.valueType === calculatorInputTypes.PART) {
      formInputs[input.key] = buildPartInput(input)
    } else {
      formInputs[input.key] = getInputRawValue(input)
    }
  })

  return formInputs
}

const buildPartInput = (input) => {
  const partId = input.partId
  const part = get(input, 'parts', null)

  if (isNil(partId))
    return null

  return {
    type: get(part, 'type'),
    id: partId,
    value: 'OR' + partId,
    label: part
      ? get(part, 'partCode', '') + ': ' + get(part, 'description', '')
      : String(partId),
    part,
  }
}

export const buildFormDefaultInputs = (formDefinition, inputs = []) => {
  const sections = get(formDefinition, 'form.sections', [])
  const defaults = {}

  sections.forEach(section => {
    getSectionInputs(section).forEach(inputName => {
      const input = findInput(formDefinition.inputs, inputName)
      if (input && input.default !== undefined) {
        defaults[input.name] = input.default
      }
    })
  })

  return {
    ...defaults,
    ...buildFormInputs(inputs),
  }
}

export const getCalculatorTextInput = (inputs = [], key, defaultValue) =>
  get(inputs.find(input => input.key === key), 'textValue') || defaultValue

export const setCalculatorTextInput = (inputs = [], key, textValue, level) => [
  ...inputs.filter(input => input.key !== key),
  {key, valueType: calculatorInputTypes.TEXT, textValue, level},
]

export const getNonFormInputs = (formDefinition, inputs = []) =>
  inputs.filter(input => isNil(findInput(get(formDefinition, 'inputs', []), input.key)))

export const getCalculatorNumberInput = (inputs = [], key, defaultValue = 0) => {
  return get(inputs.find(input => input.key === key), 'numberValue') ?? defaultValue
}

export const setCalculatorNumberInput = (inputs = [], key, numberValue, level) => {
  return [
    ...inputs.filter(input => input.key !== key),
    {key, valueType: calculatorInputTypes.NUMBER, numberValue, level},
  ]
}

export const mapPartToLineItemDetails = (part) => {
  const inputs = get(part, 'pricingCalculator.inputs', [])

  return {
    description: get(part, 'description', ''),
    notes: get(part, 'notes', ''),
    pricingMethod: getCalculatorTextInput(inputs, 'pricingMethod', PRICING_METHODS.AUTO_CALCULATED),
    waste: getCalculatorTextInput(inputs, 'waste', DEFAULT_WASTE),
    markup: getCalculatorNumberInput(inputs, 'markup'),
    margin: getCalculatorNumberInput(inputs, 'margin'),
    unitPrice: getCalculatorNumberInput(inputs, 'unitPrice'),
    totalPrice: getCalculatorNumberInput(inputs, 'totalPrice'),
    fixedPriceM2: getCalculatorNumberInput(inputs, 'fixedPriceM2'),
  }
}

export const buildPriceListPartInputs = (part, latestInputs) => {
  const partInputs = get(part, 'pricingCalculator.inputs', [])
  return isNil(latestInputs)
    ? partInputs
    : mergeLatestHigherLevelInputs(partInputs, latestInputs, 'PART')
}

export const getPartCalculator = (part) => ({
  calculatorName: get(part, 'pricingCalculator.name') || CALCULATOR_NAME,
  calculatorVersion: get(part, 'pricingCalculator.version') || CALCULATOR_VERSION,
})

export const mapPriceListPartToLineItemDetails = (part, order) => ({
  ...mapPartToLineItemDetails(part),
  partCode: get(part, 'partCode') || 'ITEM',
  taxCode: get(part, 'taxCode.id') ?? get(order, 'taxCodeId', null),
})

export const setPricingMethod = (item, pricingMethod) => {
  const inputs = get(item, 'calculatorData.inputs', [])
  const pricingMethodInput = inputs.find(input => input.key === 'pricingMethod')

  item.calculatorData = {
    ...item.calculatorData,
    inputs: pricingMethodInput
      ? inputs.map(input => input.key === 'pricingMethod'
        ? {...input, textValue: pricingMethod}
        : input)
      : [
        ...inputs,
        {
          key: 'pricingMethod',
          textValue: pricingMethod,
          valueType: calculatorInputTypes.TEXT,
        },
      ],
  }
}

const fixedInputMappings = {
  desc: 'description',
  notes: 'notes',
}

export const applyOrderItemUpdateToInput = (orderItem, columnKey, value, fieldMappings) => {
  const inputKey = getCalculatorInputName(orderItem, columnKey, fieldMappings) || fixedInputMappings[columnKey]
  if (inputKey) {
    const input = findCalculatorInput(orderItem, inputKey)
    if (input) updateCalculatorInput(input, value)
  }
}

export const findCalculatorInput = (orderItem, inputKey) => {
  const inputs = get(orderItem, 'calculatorData.inputs', [])
  return inputs.find(input => input.key === inputKey)
}

export const wrapColumnsForCalculatorInlineEdit = (columns, fieldMappings) => {
  if (!fieldMappings) return

  const allMappedColumns = new Set()
  Object.keys(fixedInputMappings).forEach(col => allMappedColumns.add(col))

  Object.values(fieldMappings).forEach(m =>
    Object.values(m).forEach(col => allMappedColumns.add(col))
  )

  Object.keys(columns).forEach(colKey => {
    if (!columns[colKey].setData) return

    if (allMappedColumns.has(colKey)) {
      const originalSetData = columns[colKey].setData

      columns[colKey].setData = (entity, value, meta) => {

        if (isPricingCalculatorEntity(entity))
          applyOrderItemUpdateToInput(entity, colKey, value, fieldMappings)

        return originalSetData(entity, value, meta)
      }
    }
  })
}

export const updateSubItemsWithPricing = (subItems, pricingSubItems, updateData, currencies, pricingAccuracy) => {
  return subItems.map(subItem => {
    const subItemPrice = pricingSubItems[subItem.id]

    if (!updateData)
      return updateOrderItemUnitValuesAreSet(subItem, subItemPrice)

    return subItemPrice
      ? getCalculatedOrderItem(subItem, currencies, null, subItemPrice, null, pricingAccuracy)
      : subItem
  })
}

export const addSubItemsForPricingCalculators = (
  filteredSubItems,
  pricingCalculatorItems,
  pricingSubItemsArray,
  packTypes = []
) => {
  return [
    ...filteredSubItems,
    ...pricingCalculatorItems.flatMap(item => pricingSubItemsArray
      .filter(subItem => subItem.parentId === `${item.id}`)
      .map(subItem => createSubItemForPricingCalculator(subItem, item, packTypes))),
  ]
}

const getCalculatorInputPart = (parentItem, partId) => {
  const input = get(parentItem, 'calculatorData.inputs', [])
    .find(i => i.valueType === calculatorInputTypes.PART && String(i.partId) === String(partId))

  return get(input, 'parts', null)
}

export const createSubItemForPricingCalculator = (pricingItem, parentItem, packTypes = []) => {
  const subGuid = uuid()
  const currencyRate = get(parentItem, 'currencyRate', 1)

  const unitCost = get(pricingItem, 'unitCost', 1)
  const unitPrice = get(pricingItem, 'unitPrice', 1)
  const totalCost = get(pricingItem, 'totalCost', 1)
  const totalPrice = get(pricingItem, 'totalPrice', 1)

  const partId = get(pricingItem, 'metadata.partId')
  const part = getCalculatorInputPart(parentItem, partId)
  const isLabour = isLabourPart(part)
  const minutesPack = isLabour ? getMinutesPackForPart(part, packTypes) : null

  return {
    ...(isLabour ? {partPackDesc: PACK_TYPE_LABOUR_LABELS.MINUTES} : {}),
    isNew: true,
    enabled: true,
    id: subGuid,
    guid: subGuid,
    parentId: parseIfNumeric(pricingItem.parentId),
    desc: get(pricingItem, 'metadata.description', ''),
    partCode: get(pricingItem, 'metadata.partCode', ''),
    part,
    partId,
    partType: get(part, 'type', null),
    partPackId: isLabour
      ? get(minutesPack, 'id', null)
      : get(getPricingPack(part), 'id', null),
    quantity: get(pricingItem, 'metadata.quantity', 1),
    unitCost,
    unitPrice,
    unitCostSet: false,
    unitPriceSet: false,
    totalCost,
    totalPrice,
    profit: round(getProfit(totalPrice, totalCost, currencyRate), 2),
    length: get(pricingItem, 'metadata.length', 0),
    width: get(pricingItem, 'metadata.width', 0),
    scalingType: ScalingType.FIXED.id,
    type: {id: OrderItemType.part},
    markup: unitCost > 0 ? round(getMarkup(unitPrice, unitCost, currencyRate), 2) : 0,
    margin: unitCost > 0 ? round(getMargin(unitPrice, unitCost, currencyRate), 2) : 0,
    taxCode: get(parentItem, 'taxCode', null),
    taxRate: get(parentItem, 'taxRate', 0),
    taxCodeId: get(parentItem, 'taxCodeId', null),
    referralFee: get(parentItem, 'referralFee', 0),
    currencyRate,
    pricingRate: 1,
  }
}

export const recordDeletedSubItems = (pricingCalculatorItems, updatedSubItems, deletedSubItemIds) => {
  pricingCalculatorItems.forEach(item => {
    item.deletedSubItems = [
      ...new Set([
        ...item.deletedSubItems || [],
        ...updatedSubItems
          .filter(subItem => subItem.parentId === item.id && deletedSubItemIds.includes(subItem.id))
          .filter(isSavedSubItem)
          .map(subItem => subItem.id),
      ]),
    ]
  })
}

export const getPartInputAllowedTypes = (input) => {
  const partTypes = get(input, 'options.partTypes', [])
  if (isNil(partTypes))
    return null

  return partTypes
    .map(partTypeName => Object.values(PartType)
      .find(partType => partType.label.toLowerCase() === partTypeName.toLowerCase()))
    .filter(partType => !isNil(partType))
    .map(partType => partType.id)
}

export const EDGES = ['top', 'bottom', 'left', 'right']

export const parseEdgeSelection = (value) => {
  const selected = String(value || '').split(',').map(edge => edge.trim())
  return EDGES.filter(edge => selected.includes(edge))
}

export const toggleEdgeSelection = (value, edge) => {
  const selected = parseEdgeSelection(value)
  const updated = selected.includes(edge)
    ? selected.filter(e => e !== edge)
    : [...selected, edge]

  return EDGES.filter(e => updated.includes(e)).join(',')
}

export const getEdgeDimensions = (input, formValues) => {
  const length = parseFloat(get(formValues, get(input, 'options.lengthFrom')))
  const width = parseFloat(get(formValues, get(input, 'options.widthFrom')))
  const sides = [length, width].filter(side => !isNaN(side))
  const longest = isEmpty(sides) ? null : Math.max(...sides)
  const shortest = isEmpty(sides) ? null : Math.min(...sides)

  return {top: longest, bottom: longest, left: shortest, right: shortest}
}

export const getPricingCalculatorButtonClass = (label, mode) => {
  const context = mode === PricingCalculatorContext.BUILDER ? 'builder-' : ''

  return `pricing-calculator-${context}${kebabCase(label)}`
}

export const getCalculatorNameForItem = (orderItem) => {
  return get(orderItem, 'calculatorData.name', '')
}

export const isPricingCalculatorActive = (user, calculatorName) => {
  if (!isPricingCalculatorFeatureToggleEnabled(user))
    return false

  if (!accountHasFeature(user, features.PRICING_CALCULATORS))
    return false

  return accountHasCalculator(user, calculatorName)
}

export const isPricingCalculatorsActive = (user) => {

  if (!isPricingCalculatorFeatureToggleEnabled(user))
    return false

  if (!accountHasFeature(user, features.PRICING_CALCULATORS))
    return false

  // TODO: Hard-coded pricing calculator name here
  return accountHasCalculator(user, CALCULATOR_NAME)
}

export const canAddPriceListPartToQuote = (part, user) => {
  const partType = get(part, 'type')
  if (!isPartTypePricingCalculator(partType))
    return canAddItemOfType(partType, user)

  return isPricingCalculatorPriceListEnabled(user)
    && isPricingCalculatorActive(user, getPartCalculator(part).calculatorName)
}